import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/App';
import Layout from '@/components/Layout';
import {
  Shield,
  Gem,
  ArrowRight,
  PlusCircle,
  History,
  MessageSquare,
  Lightbulb,
  Bug,
  Send,
  CheckCircle,
  Clock,
  Eye,
  X,
  Upload,
  Sparkles,
  Target
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const statusConfig = {
  pending: { label: 'Pendiente', color: 'amber', icon: Clock },
  in_progress: { label: 'En Revisión', color: 'blue', icon: Eye },
  resolved: { label: 'Resuelto', color: 'green', icon: CheckCircle },
  closed: { label: 'Cerrado', color: 'slate', icon: X }
};

const FeedbackSupport = () => {
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isSupportPath = location.pathname === '/support';
  
  const [loading, setLoading] = useState(false);
  const [myFeedback, setMyFeedback] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  const typeParam = searchParams.get('type');
  const [activeSubTab, setActiveSubTab] = useState('new');

  const [formData, setFormData] = useState({
    type: typeParam || (isSupportPath ? 'bug' : 'complaint'),
    subject: '',
    description: '',
    screenshot_url: ''
  });

  const feedbackTypes = [
    { value: 'complaint', label: 'Queja', icon: Target, color: 'purple', emoji: '😤' },
    { value: 'suggestion', label: 'Sugerencia', icon: Lightbulb, color: 'amber', emoji: '💡' },
    { value: 'bug', label: 'Reporte de Bug', icon: Bug, color: 'red', emoji: '🐛' },
    { value: 'other', label: 'Otro', icon: MessageSquare, color: 'slate', emoji: '💬' }
  ];

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    try {
      setLoadingHistory(true);
      const res = await axios.get(`${API}/feedback/my-feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyFeedback(res.data);
    } catch (error) {
      console.error('Error fetching feedback history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const data = new FormData();
      data.append('file', file);

      const res = await axios.post(`${API}/upload`, data, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      
      setFormData(prev => ({ ...prev, screenshot_url: res.data.url }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir la imagen.');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, screenshot_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;

    try {
      setLoading(true);
      await axios.post(`${API}/feedback`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData({
        type: isSupportPath ? 'bug' : 'complaint',
        subject: '',
        description: '',
        screenshot_url: ''
      });
      
      setActiveSubTab('history');
      fetchHistory();
      alert('¡Enviado con éxito!');
    } catch (error) {
      console.error('Error sending feedback:', error);
      alert('Error al enviar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-3 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
          
          {/* Header Section */}
          <div className="text-center pt-2">
            <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${isSupportPath ? 'from-blue-500 to-indigo-600' : 'from-purple-500 to-pink-600'} rounded-xl sm:rounded-2xl mb-2 sm:mb-3 shadow-md ring-2 ring-white dark:ring-slate-800`}>
              {isSupportPath ? <Shield size={24} className="text-white" /> : <MessageSquare size={24} className="text-white" />}
            </div>
            <h1 className="text-xl sm:text-3xl font-black text-slate-900 dark:text-white mb-0.5 tracking-tight">
              {isSupportPath ? 'Soporte Técnico' : 'Feedback y Opinión'}
            </h1>
            <p className="text-[10px] sm:text-sm text-slate-500 dark:text-slate-400 max-w-lg mx-auto font-bold uppercase tracking-wider">
              {isSupportPath 
                ? 'Reporta errores o sugiere mejoras' 
                : 'Tu opinión nos ayuda a mejorar'}
            </p>
          </div>

          {/* Sub-Tabs - Mobile Centric */}
          <div className="flex justify-center">
            <div className="inline-flex w-full sm:w-auto p-1.5 bg-slate-200 dark:bg-slate-800 rounded-2xl border border-slate-300 dark:border-slate-700">
              <button
                onClick={() => setActiveSubTab('new')}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeSubTab === 'new'
                  ? `bg-white dark:bg-slate-700 text-${isSupportPath ? 'blue' : 'purple'}-600 shadow-md`
                  : 'text-slate-500'
                  }`}
              >
                <PlusCircle size={16} />
                <span>Nuevo</span>
              </button>
              <button
                onClick={() => setActiveSubTab('history')}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${activeSubTab === 'history'
                  ? `bg-white dark:bg-slate-700 text-${isSupportPath ? 'blue' : 'purple'}-600 shadow-md`
                  : 'text-slate-500'
                  }`}
              >
                <History size={16} />
                <span>Historial</span>
              </button>
            </div>
          </div>

          {activeSubTab === 'history' ? (
            /* History Content */
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-2xl overflow-hidden border border-slate-100 dark:border-slate-700">
                  <div className={`bg-gradient-to-r ${isSupportPath ? 'from-slate-800 to-slate-900' : 'from-slate-700 to-slate-800'} px-6 py-4`}>
                    <h2 className="text-lg font-black text-white flex items-center gap-3">
                      <History size={20} className="text-slate-400" />
                      Mis Seguimientos
                    </h2>
                  </div>

                  <div className="p-3 sm:p-6 space-y-4">
                    {loadingHistory ? (
                      <div className="text-center py-10">
                        <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
                      </div>
                    ) : myFeedback.filter(f => isSupportPath ? (f.type === 'bug' || f.type === 'suggestion') : (f.type === 'complaint' || f.type === 'other')).length === 0 ? (
                      <div className="text-center py-10">
                        <MessageSquare size={48} className="mx-auto text-slate-200 mb-2" />
                        <p className="text-slate-400 text-xs font-bold">Sin registros aún.</p>
                      </div>
                    ) : (
                      myFeedback
                        .filter(f => isSupportPath ? (f.type === 'bug' || f.type === 'suggestion') : (f.type === 'complaint' || f.type === 'other'))
                        .map((item) => {
                          const type = feedbackTypes.find(t => t.value === item.type);
                          const status = statusConfig[item.status] || statusConfig.pending;
                          const StatusIcon = status.icon;

                          return (
                            <div key={item.id} className="border border-slate-100 dark:border-slate-700 rounded-2xl p-4 hover:bg-slate-50 transition-all shadow-sm">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-xl">{type?.emoji}</div>
                                  <div>
                                    <h3 className="font-black text-slate-800 dark:text-white text-sm">{item.subject}</h3>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                      {type?.label} • {new Date(item.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-black uppercase bg-${status.color}-100 text-${status.color}-700`}>
                                  <StatusIcon size={10} />
                                  {status.label}
                                </div>
                              </div>
                              <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl text-xs text-slate-600 dark:text-slate-300">
                                {item.description}
                              </div>
                            {item.screenshot_url && (
                              <img src={item.screenshot_url} alt="Evidencia" className="mt-4 max-h-40 rounded-xl border border-slate-200" />
                            )}
                            {item.admin_response && (
                              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-2xl">
                                <p className="text-[10px] font-black text-blue-800 uppercase mb-1 flex items-center gap-2">
                                  <Sparkles size={12} /> Respuesta de Soporte
                                </p>
                                <p className="text-sm text-blue-900 dark:text-blue-200 italic font-medium">"{item.admin_response}"</p>
                              </div>
                            )}
                          </div>
                        );
                      })
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Form Content */
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-100 dark:border-slate-700">
                <div className={`bg-gradient-to-r ${isSupportPath ? 'from-blue-600 to-indigo-700' : 'from-purple-600 to-pink-600'} px-8 py-6 text-white`}>
                  <h2 className="text-xl font-black flex items-center gap-3">
                    <PlusCircle size={24} />
                    {isSupportPath ? 'Nuevo Reporte Técnico' : 'Nueva Opinión'}
                  </h2>
                </div>

                <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-4 sm:space-y-6">
                  {/* Category Selection */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    {feedbackTypes
                      .filter(t => isSupportPath ? (t.value === 'bug' || t.value === 'suggestion') : (t.value === 'complaint' || t.value === 'other'))
                      .map((type) => {
                        const TypeIcon = type.icon;
                        const isSelected = formData.type === type.value;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, type: type.value }))}
                            className={`flex flex-col items-center gap-2 p-3 sm:p-5 rounded-[1.5rem] sm:rounded-[2rem] border-2 transition-all ${isSelected
                              ? `border-${isSupportPath ? 'blue' : 'purple'}-500 bg-${isSupportPath ? 'blue' : 'purple'}-50`
                              : 'border-slate-100 hover:border-slate-200'
                              }`}
                          >
                            <div className={`p-3 rounded-xl ${isSelected ? `bg-${isSupportPath ? 'blue' : 'purple'}-500 text-white` : 'bg-white text-slate-300 shadow-sm'}`}>
                              <TypeIcon size={20} />
                            </div>
                            <span className={`text-[10px] sm:text-xs font-black uppercase tracking-widest ${isSelected ? 'text-slate-800' : 'text-slate-400'}`}>{type.label}</span>
                          </button>
                        );
                      })}
                  </div>

                  {/* Inputs */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Asunto *</label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={e => setFormData(p => ({ ...p, subject: e.target.value }))}
                        className="w-full px-4 py-3 sm:py-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl sm:rounded-2xl border-2 border-transparent focus:border-blue-500 focus:bg-white transition-all outline-none font-bold text-sm sm:text-base"
                        placeholder="Resumen del reporte..."
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Descripción *</label>
                      <textarea
                        required
                        value={formData.description}
                        onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                        rows={4}
                        className="w-full px-4 py-3 sm:py-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl sm:rounded-2xl border-2 border-transparent focus:border-blue-500 focus:bg-white transition-all outline-none font-medium resize-none text-sm sm:text-base"
                        placeholder="Detalles del reporte..."
                      />
                    </div>
                  </div>

                  {/* Upload */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Evidencia (Opcional)</label>
                    {!formData.screenshot_url ? (
                      <div className="relative">
                        <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="file-up" ref={fileInputRef} />
                        <label htmlFor="file-up" className="flex flex-col items-center justify-center p-8 border-4 border-dashed border-slate-100 rounded-[2rem] cursor-pointer hover:bg-slate-50 transition-all">
                          {uploadingImage ? <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /> : <Upload size={32} className="text-slate-300 mb-2" />}
                          <p className="text-xs font-black text-slate-400 uppercase">Subir captura</p>
                        </label>
                      </div>
                    ) : (
                      <div className="relative p-4 bg-slate-50 rounded-[2rem] border-2 border-slate-100">
                        <img src={formData.screenshot_url} alt="Preview" className="w-full max-h-48 object-contain rounded-xl mb-2" />
                        <button type="button" onClick={removeImage} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full shadow-lg"><X size={16} /></button>
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-5 bg-gradient-to-r ${isSupportPath ? 'from-blue-600 to-indigo-700 shadow-blue-100' : 'from-purple-600 to-pink-600 shadow-purple-100'} text-white rounded-2xl font-black uppercase tracking-widest shadow-xl flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50`}
                  >
                    {loading ? <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" /> : <><Send size={20} /> Enviar Reporte</>}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Bloque VIP Destacado - Solo en Soporte */}
          {isSupportPath && (
            <div 
              className="w-full p-5 sm:p-8 bg-gradient-to-br from-blue-600 to-indigo-800 rounded-[2rem] sm:rounded-[2.5rem] text-white flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl cursor-pointer hover:brightness-110 transition-all group"
              onClick={() => window.location.href = 'mailto:soporte@capacitatu.com'}
            >
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-xl border border-white/30 group-hover:rotate-12 transition-transform">
                  <Gem size={28} />
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="text-lg sm:text-xl font-black">Soporte VIP</h3>
                  <p className="text-blue-100 text-xs sm:text-sm font-medium">Prioridad 24/7 para tu tranquilidad</p>
                </div>
              </div>
              <div className="px-6 py-2.5 bg-white text-blue-600 rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg">
                Contactar
              </div>
            </div>
          )}

          {/* Footer Info */}
          <div className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-md p-6 rounded-[2rem] border-2 border-slate-100 dark:border-slate-700 flex items-start gap-4">
            <MessageSquare className="text-blue-600 mt-1" />
            <div>
              <h4 className="font-black text-slate-800 dark:text-white">¿Ayuda inmediata?</h4>
              <p className="text-sm text-slate-500">Usa el Chat IA o escribe a soporte@capacitatu.com</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FeedbackSupport;
